<template>
  <b-modal
    id="email-modal"
    ref="emailModal"
    v-model="showModal"
    title="Send Email"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <form @submit.prevent="sendEmail">
      <!-- Recipient Section -->
      <div class="mb-3">
        <label class="form-label fw-bold">
          <b-icon icon="person-circle" class="mr-2"></b-icon>
          To:
        </label>
        <div
          v-if="recipient && recipient.email"
          class="recipient-info p-2 bg-light rounded"
        >
          <div class="d-flex align-items-center">
            <b-avatar size="sm" :src="recipient.avatar" class="mr-2"></b-avatar>
            <div>
              <div class="fw-bold">{{ recipient.surname }}</div>
              <small class="text-muted">{{ recipient.email }}</small>
            </div>
          </div>
        </div>
        <div v-else>
          <b-form-input
            v-model="emailForm.to"
            type="email"
            placeholder="Enter recipient email"
            required
          />
        </div>
      </div>

      <!-- Subject -->
      <div class="mb-3">
        <label class="form-label fw-bold">
          <b-icon icon="tag" class="mr-2"></b-icon>
          Subject:
        </label>
        <b-form-input
          v-model="emailForm.subject"
          placeholder="Enter email subject"
          required
        />
      </div>

      <!-- Email Templates (Optional) -->
      <div class="mb-3" v-if="showTemplates && templates.length > 0">
        <label class="form-label fw-bold">
          <b-icon icon="file-earmark-text" class="mr-2"></b-icon>
          Quick Templates:
        </label>
        <b-form-select
          v-model="selectedTemplate"
          :options="templateOptions"
          @change="loadTemplate"
        >
          <template #first>
            <b-form-select-option value="" disabled>
              Select a template
            </b-form-select-option>
          </template>
        </b-form-select>
      </div>

      <!-- Message Body -->
      <div class="mb-3">
        <label class="form-label fw-bold">
          <b-icon icon="chat-text" class="mr-2"></b-icon>
          Message:
        </label>
        <b-form-textarea
          v-model="emailForm.message"
          rows="8"
          placeholder="Type your message here..."
          required
        />
      </div>

      <!-- Attachments (Optional) -->
      <div class="mb-3" v-if="allowAttachments">
        <label class="form-label fw-bold">
          <b-icon icon="paperclip" class="mr-2"></b-icon>
          Attachments:
        </label>
        <b-form-file
          v-model="emailForm.attachments"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          placeholder="Choose files or drop them here..."
          drop-placeholder="Drop files here..."
        />
        <small class="text-muted">
          Allowed: PDF, Word documents, Images (Max 10MB per file)
        </small>
      </div>

      <!-- Priority Level -->
      <div class="mb-3">
        <label class="form-label fw-bold">
          <b-icon icon="exclamation-circle" class="mr-2"></b-icon>
          Priority:
        </label>
        <b-form-select v-model="emailForm.priority">
          <b-form-select-option value="low">Low</b-form-select-option>
          <b-form-select-option value="normal">Normal</b-form-select-option>
          <b-form-select-option value="high">High</b-form-select-option>
          <b-form-select-option value="urgent">Urgent</b-form-select-option>
        </b-form-select>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <!-- Draft Button -->
          <button
            type="button"
            class="btn btn-outline-secondary me-2"
            @click="saveDraft"
            :disabled="isLoading"
          >
            <b-icon icon="file-earmark" class="mr-1"></b-icon>
            Save Draft
          </button>
        </div>

        <div>
          <!-- Cancel Button -->
          <button
            type="button"
            class="btn btn-outline-danger me-2"
            @click="closeModal"
            :disabled="isLoading"
          >
            Cancel
          </button>

          <!-- Send Button -->
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isLoading || !isFormValid"
          >
            <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
            <b-icon v-else icon="send" class="mr-1"></b-icon>
            {{ isLoading ? "Sending..." : "Send Email" }}
          </button>
        </div>
      </div>
    </form>
  </b-modal>
</template>

<script>
export default {
  name: "EmailComponent",
  props: {
    // Recipient information
    recipient: {
      type: Object,
      default: () => ({
        name: "",
        email: "",
        avatar: "",
        surname: "",
      }),
    },
    // Pre-fill subject
    defaultSubject: {
      type: String,
      default: "",
    },
    // Pre-fill message
    defaultMessage: {
      type: String,
      default: "",
    },
    // Show email templates
    showTemplates: {
      type: Boolean,
      default: true,
    },
    // Allow file attachments
    allowAttachments: {
      type: Boolean,
      default: true,
    },
    // Email templates
    templates: {
      type: Array,
      default: () => [],
    },
    // API endpoint for sending emails
    apiEndpoint: {
      type: String,
      default: "api/send-email",
    },
  },
  data() {
    return {
      showModal: false,
      isLoading: false,
      selectedTemplate: "",
      emailForm: {
        to: "",
        subject: "",
        message: "",
        priority: "normal",
        attachments: null,
      },
    };
  },
  computed: {
    templateOptions() {
      return this.templates.map((template) => ({
        value: template.id,
        text: template.name,
      }));
    },
    isFormValid() {
      const hasRecipient =
        (this.recipient && this.recipient.email) || this.emailForm.to;
      return hasRecipient && this.emailForm.subject && this.emailForm.message;
    },
  },
  methods: {
    // Open modal and optionally pre-fill data
    openModal(data = {}) {
      this.resetForm();

      // Pre-fill form with provided data
      if (data.subject) this.emailForm.subject = data.subject;
      if (data.message) this.emailForm.message = data.message;
      if (data.to) this.emailForm.to = data.to;

      // Use default props if no data provided
      if (!data.subject && this.defaultSubject) {
        this.emailForm.subject = this.defaultSubject;
      }
      if (!data.message && this.defaultMessage) {
        this.emailForm.message = this.defaultMessage;
      }

      this.showModal = true;
    },

    // Close modal
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    // Reset form data
    resetForm() {
      this.emailForm = {
        to: "",
        subject: "",
        message: "",
        priority: "normal",
        attachments: null,
      };
      this.selectedTemplate = "";
      this.isLoading = false;
    },

    // Load selected template
    loadTemplate() {
      const template = this.templates.find(
        (t) => t.id === this.selectedTemplate
      );
      if (template) {
        this.emailForm.subject = template.subject || this.emailForm.subject;
        this.emailForm.message = template.content || this.emailForm.message;
      }
    },

    // Send email
    async sendEmail() {
      if (!this.isFormValid) return;

      this.isLoading = true;

      try {
        const formData = new FormData();

        // Add basic email data
        formData.append("to", this.recipient?.email || this.emailForm.to);
        formData.append("subject", this.emailForm.subject);
        formData.append("message", this.emailForm.message);
        formData.append("priority", this.emailForm.priority);

        // Add attachments if any
        if (
          this.emailForm.attachments &&
          this.emailForm.attachments.length > 0
        ) {
          Array.from(this.emailForm.attachments).forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }

        // Send email via API
        const response = await this.$store.dispatch(
          "AdmissionOfficer/sendEmail",
          formData
        );
        console.log("Email send response:", response.data);
        if (response.data.status === "success") {
          this.$emit("email-sent", {
            recipient: this.recipient?.email || this.emailForm.to,
            subject: this.emailForm.subject,
            timestamp: new Date(),
          });

          Toast.fire({
            icon: "success",
            title: "Email sent successfully!",
          });
          this.closeModal();
        } else {
          throw new Error(response.message || "Failed to send email");
        }
      } catch (error) {
        Toast.fire({
          icon: "error",
          title: error.message || "Failed to send email",
        });
      } finally {
        this.isLoading = false;
        this.$store.dispatch("spinner/hideSpinner");
      }
    },

    // Save as draft
    async saveDraft() {
      try {
        const draftData = {
          to: this.recipient?.email || this.emailForm.to,
          subject: this.emailForm.subject,
          message: this.emailForm.message,
          priority: this.emailForm.priority,
        };

        await this.$store.dispatch("Email/saveDraft", draftData);

        Toast.fire({
          icon: "info",
          title: "Draft saved successfully!",
        });
      } catch (error) {
        Toast.fire({
          icon: "error",
          title: "Failed to save draft",
        });
      }
    },
  },
  created() {
    // Listen for email events
    Fire.$on("SEND-EMAIL", (data) => {
      this.openModal(data);
    });

    Fire.$on("SEND-EMAIL-TO-RECIPIENT", (recipientData) => {
      this.openModal({
        to: recipientData.email,
        subject: recipientData.defaultSubject || "",
        message: recipientData.defaultMessage || "",
      });
    });
  },
  beforeDestroy() {
    // Clean up event listeners
    Fire.$off("SEND-EMAIL");
    Fire.$off("SEND-EMAIL-TO-RECIPIENT");
  },
};
</script>

<style scoped>
.recipient-info {
  border: 1px solid #dee2e6;
}

.form-label {
  margin-bottom: 0.5rem;
  color: #495057;
}

.fw-bold {
  font-weight: 600 !important;
}

.me-2 {
  margin-right: 0.5rem !important;
}

/* Custom file input styling */
.custom-file-label::after {
  content: "Browse";
}

/* Priority styling */
.form-select option[value="urgent"] {
  color: #dc3545;
  font-weight: bold;
}

.form-select option[value="high"] {
  color: #fd7e14;
}

.form-select option[value="normal"] {
  color: #6c757d;
}

.form-select option[value="low"] {
  color: #198754;
}
</style>
